import { InvalidateOptions, QueryClient, InvalidateQueryFilters } from "@tanstack/react-query";
import { QueryMethodForModel, ModelName, QueryDataForMethod, TellescopeQueryKey, ParamsForMethod } from "../../types";
import { TellescopeInvalidateQueryFilters } from "../../hooks";
import { buildTellescopeQueryKeyFilter } from "../../keys";

/**
 * Arguments for invalidating queries in cache.
 *
 * @template Model - The Tellescope model name
 * @template Method - The query method for the model
 * @template TError - The error type for the queries
 */
export interface InvalidateTellescopeQueriesArgs<
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
> extends Omit<
    TellescopeInvalidateQueryFilters<
      QueryDataForMethod<Model, Method>,
      TError,
      QueryDataForMethod<Model, Method>,
      TellescopeQueryKey<Model, Method>
    >,
    "queryKey"
  > {
  /** The Tellescope model name */
  model: Model;
  /** Query method name */
  method: Method;
  /** Optional request parameters to narrow down queries. If omitted, invalidates all queries for the method. */
  request?: Partial<ParamsForMethod<Model, Method>>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /** Optional invalidate options (e.g., throwOnError, cancelRefetch) */
  options?: InvalidateOptions;
}

/**
 * Invalidates queries in the query client cache for matching Tellescope queries.
 * Invalidated queries will be marked as stale and refetched in the background if they are currently being rendered.
 *
 * @param args - The invalidate queries arguments
 * @returns A promise that resolves when invalidation is complete
 *
 * @example
 * ```tsx
 * // Invalidate all queries for a method
 * await invalidateTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 * });
 *
 * // Invalidate specific query
 * await invalidateTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 * });
 *
 * // Invalidate with filters
 * await invalidateTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getSome",
 *   filters: {
 *     refetchType: "active", // Only refetch active queries
 *   },
 * });
 *
 * // Invalidate without automatic refetch
 * await invalidateTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 *   filters: {
 *     refetchType: "none", // Don't refetch, just mark as stale
 *   },
 * });
 *
 * // Invalidate with custom options
 * await invalidateTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 *   options: {
 *     throwOnError: true, // Throw error if refetch fails
 *     cancelRefetch: false, // Don't cancel in-flight refetches
 *   },
 * });
 * ```
 */
export const invalidateTellescopeQueries = async <
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
>({
  model,
  method,
  request,
  queryClient,
  options,
  ...filters
}: InvalidateTellescopeQueriesArgs<Model, Method, TError>) => {
  const queryKey = buildTellescopeQueryKeyFilter({ model, method, request });

  return queryClient.invalidateQueries(
    {
      queryKey,
      ...filters,
    } as InvalidateQueryFilters<TellescopeQueryKey<Model, Method>>,
    options
  );
};
